import { ReactNode, useEffect } from "react";
import { createPortal } from "react-dom";

import { Icon } from "./Icon";

type Props = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  tone?: "default" | "danger" | "warn" | "success";
  children: ReactNode;
  footer?: ReactNode;
};

export function Modal({ open, onClose, title, tone = "default", children, footer }: Props) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const accent =
    tone === "danger"
      ? "var(--tcg-danger)"
      : tone === "warn"
        ? "var(--tcg-warn)"
        : tone === "success"
          ? "var(--tcg-success)"
          : "var(--tcg-navy)";

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(14, 31, 79, 0.55)",
        zIndex: 100,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        padding: 12,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--tcg-card)",
          borderRadius: 20,
          width: "100%",
          maxWidth: 420,
          padding: 0,
          maxHeight: "90dvh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 20px 60px -20px rgba(14, 31, 79, 0.6)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "16px 18px 12px",
            borderBottom: "1px solid var(--tcg-border)",
          }}
        >
          <div
            style={{
              flex: 1,
              fontSize: 16,
              fontWeight: 900,
              letterSpacing: "-0.01em",
              color: accent,
              lineHeight: 1.25,
            }}
          >
            {title}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="chiudi"
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "var(--tcg-muted)",
              padding: 6,
              borderRadius: 8,
            }}
          >
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={{ padding: "14px 18px", overflowY: "auto" }}>{children}</div>
        {footer && (
          <div
            style={{
              padding: "12px 18px 14px",
              borderTop: "1px solid var(--tcg-border)",
              display: "flex",
              gap: 8,
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
